<template>
  <!-- Added title props to prevent browser tooltip from showing as it will block the carbon tooltip -->
  <cv-button
    class="tooltip-button"
    :class="{
      '_has-tooltip': showTooltip,
      'bx--btn--icon-only': iconOnly,
    }"
    title
    :kind="kind"
    :size="size"
    :disabled="disabled"
    v-bind="attrs"
  >
    <cv-tooltip
      v-if="showTooltip"
      :tip="tip"
      :alignment="validatedAlignment"
      :direction="validatedDirection"
    >
    </cv-tooltip>
    <slot />
  </cv-button>
</template>

<script setup lang="ts">
import { computed, useAttrs } from "vue";

// Props
interface Props {
  kind?: string;
  size?: string;
  // Default value for disabled is true as that's the common use case for this component
  // In most circumstances, the <cv-button> or <fetch-data-button> would be used instead
  disabled?: boolean;
  tip?: string;
  direction?: string;
  alignment?: string;
  showTooltip?: boolean;
  iconOnly?: boolean;
}

const {
  kind = "primary",
  size = "",
  disabled = true,
  tip = "",
  direction = "bottom",
  alignment = "center",
  showTooltip = true,
  iconOnly = false,
} = defineProps<Props>();

// Composables
const attrs = useAttrs();

// Computed
const validatedDirection = computed(() => {
  return validateDirection(direction);
});

const validatedAlignment = computed(() => {
  return validateAlignment(alignment);
});

// Methods
const validateDirection = (value: string): string => {
  const validDirections = ["top", "right", "bottom", "left"];
  return validDirections.includes(value) ? value : "bottom";
};

const validateAlignment = (value: string): string => {
  const validAlignments = ["center", "start", "end"];
  return validAlignments.includes(value) ? value : "center";
};
</script>

<style lang="scss">
@use "@styles/abstracts/mixins" as *;
@use "@styles/themes/sls-theme/tokens/colors" as *;
@use "@styles/themes/sls-theme/tokens/spacing" as *;
@use "@styles/themes/sls-theme/tokens/typo" as *;
@use "@styles/layout/z-map" as *;
.bx--btn.tooltip-button {
  // Fixed height to prevent jiterring when the tooltip gets appended whenever the v-if condition is fulfilled
  height: 3rem;

  &.bx--btn--field {
    height: 2.5rem;
  }

  &.bx--btn--sm {
    height: 2rem;

    .bx--tooltip__trigger.bx--tooltip--a11y {
      // to fix font-size override issue for small button tooltip
      font-size: inherit;
    }
  }

  &.bx--btn--icon-only {
    width: 40px;
    height: 40px;
    padding: 0;

    .bx--tooltip__trigger {
      z-index: calc(z-map("default") + 1);
    }
  }

  &[disabled] {
    opacity: 1;
  }

  .bx--tooltip__trigger.bx--tooltip--a11y {
    @include carbon--type-style("button-01");
    position: absolute;
    width: 100%;
    height: 100%;
    text-transform: uppercase;
    color: inherit;
    cursor: inherit;

    &.bx--tooltip--bottom {
      &:before {
        bottom: 0.25rem;
      }

      .bx--assistive-text {
        bottom: 0;
      }
    }

    &.bx--tooltip--top {
      &:before {
        top: 0.3rem;
      }

      .bx--assistive-text {
        top: 0;
      }
    }

    .bx--assistive-text {
      @include line-clamp(4);
      @include carbon--type-style("label-02", true);
      text-transform: none;

      // The line clamp would not cover the bottom portion of the clamped text as there is padding bottom
      // which is inclusive of its overflow. Hence a pseudo element is used to cover the text
      &::after {
        content: "";
        position: absolute;
        bottom: -0.5rem;
        left: 0;
        width: 100%;
        height: 0.5rem;
        background-color: $inverse-02;
      }
    }
  }
}
</style>
